import React, { useState, useEffect, useMemo } from 'react';
import './Workspace.css';
import { getDemoPayload } from './demoData';
import CreatingView from './CreatingView';

const FieldRow = ({ field, categoryId, isActive, shouldAutoFocus, onInteract, onSubmit, onClear }) => {
  const [localValue, setLocalValue] = useState(field.value || '');

  // Sync local state with parent state when field.value changes
  useEffect(() => {
    setLocalValue(field.value || '');
  }, [field.value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit(categoryId, field.key, localValue, field.label);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (onClear) {
      onClear(categoryId, field.key);
    }
  };

  // Check if field has actual content (handles both strings and arrays)
  const hasValue = Array.isArray(field.value)
    ? field.value.length > 0
    : field.value && field.value.trim() !== '';

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onInteract(); }}
      className={`field-row ${isActive ? 'active' : ''}`}
    >
      <div className="field-row-header">
        <label className="field-row-label">
          {field.label}
        </label>
        {isActive && hasValue && (
          <button onClick={handleClose} className="field-row-close">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M3 7.5L6 4.5L9 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
      <div className="field-row-content">
        {isActive ? (
          <div className="field-row-input-wrapper">
            <input
              type={field.type === 'textarea' ? 'text' : field.type}
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={shouldAutoFocus}
              className="field-row-input"
              placeholder={field.placeholder}
            />
          </div>
        ) : (
          <div className="field-row-value">{field.value || ""}</div>
        )}
      </div>
      {hasValue && !isActive && (
        <div className="field-row-check">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
    </div>
  );
};

// Helper function to render markdown-like formatting
const renderMessageContent = (content) => {
  // Split by ** to find bold text
  const parts = content.split(/(\*\*[^*]+\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Bold text
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const Workspace = ({ onBack, onCreatePresentation, documentTitle = 'Strategic Partnership Proposal', isLoading = false, onLoadingComplete, onLogoClick }) => {
  const [copied, setCopied] = useState(false);
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialLoadingReady, setInitialLoadingReady] = useState(false);
  const [onboardingExiting, setOnboardingExiting] = useState(false);
  const [loadingKey, setLoadingKey] = useState(0);

  // Pre-generate random delays for dot grid
  const dotsPerGroup = 96; // 32 cols x 3 rows
  const dotDelays = useMemo(() =>
    Array.from({ length: 6 * 96 }, () => -(Math.random() * 2)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingKey]
  );

  // Wandering star positions - each star fades in/out then moves to a new cell
  const totalDots = 6 * dotsPerGroup;
  const starCount = 8;
  const starCycleDuration = 3000;

  const [starPositions, setStarPositions] = useState([]);

  useEffect(() => {
    if (!initialLoading || initialLoadingReady) return;

    const timeoutRefs = [];

    // Stagger initial star appearances, then cycle them
    for (let i = 0; i < starCount; i++) {
      const appearDelay = 500 + i * (starCycleDuration / starCount);
      const t = setTimeout(() => {
        // Add this star at a random cell
        setStarPositions(prev => {
          const usedCells = new Set(prev.map(s => s.cellIdx));
          let newIdx;
          do { newIdx = Math.floor(Math.random() * totalDots); } while (usedCells.has(newIdx));
          return [...prev, { cellIdx: newIdx, key: Math.random() }];
        });

        // Start cycling this star after its first animation completes
        let cycleTimeout = setTimeout(function cycle() {
          setStarPositions(prev => {
            const next = [...prev];
            if (!next[i]) return prev;
            const usedCells = new Set(next.map(s => s.cellIdx));
            let newIdx;
            do { newIdx = Math.floor(Math.random() * totalDots); } while (usedCells.has(newIdx));
            next[i] = { cellIdx: newIdx, key: Math.random() };
            return next;
          });
          cycleTimeout = setTimeout(cycle, starCycleDuration);
          timeoutRefs.push(cycleTimeout);
        }, starCycleDuration);
        timeoutRefs.push(cycleTimeout);
      }, appearDelay);
      timeoutRefs.push(t);
    }

    return () => timeoutRefs.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoading, initialLoadingReady, loadingKey]);

  // Build a lookup of which cells are stars
  const starCellMap = useMemo(() => {
    const map = {};
    starPositions.forEach(s => { map[s.cellIdx] = s.key; });
    return map;
  }, [starPositions]);

  const handleCopyLink = () => {
    const shareUrl = window.location.origin + window.location.pathname;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLinkedInShare = () => {
    const shareUrl = window.location.origin + window.location.pathname;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  // Helper function to convert theme title to camelCase key
  const titleToCamelCase = (title) => {
    const words = title.split(/\s+/);
    return words[0].toLowerCase() + words.slice(1).map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join('');
  };

  const [expandedThemes, setExpandedThemes] = useState([1]);
  const [focusedField, setFocusedField] = useState(null);
  const [showStickyToolbar, setShowStickyToolbar] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);

  const [formData, setFormData] = useState({
    clientInformation: {
      companyName: '',
      industry: '',
      contactPerson: ''
    },
    documentContext: {
      purpose: '',
      background: ''
    },
    projectScope: {
      objectives: '',
      deliverables: [],
      timeline: ''
    },
    thematicSections: {
      sections: []
    },
    teamDetails: {
      teamLead: '',
      teamSize: ''
    }
  });

  const themes = [
    {
      id: 1,
      title: 'Client information',
      description: 'Details about the client and their organization.',
      fields: [
        { key: 'companyName', label: 'Company name', type: 'text', placeholder: 'Type company name...' },
        { key: 'industry', label: 'Industry', type: 'text', placeholder: 'Type industry...' },
        { key: 'contactPerson', label: 'Contact person', type: 'text', placeholder: 'Type contact person...' }
      ]
    },
    {
      id: 2,
      title: 'Document context',
      description: 'Purpose and background of this proposal.',
      fields: [
        { key: 'purpose', label: 'Purpose', type: 'textarea', placeholder: 'Describe the purpose...' },
        { key: 'background', label: 'Background', type: 'textarea', placeholder: 'Provide background information...' }
      ]
    },
    {
      id: 3,
      title: 'Project scope',
      description: 'Define what this partnership will accomplish.',
      fields: [
        { key: 'objectives', label: 'Objectives', type: 'textarea', placeholder: 'List the main objectives...' },
        { key: 'deliverables', label: 'Key deliverables', type: 'multiselect', options: ['Supply Chain Audit', 'Process Optimization', 'Training Program', 'Technical Support', 'Consulting Services'] },
        { key: 'timeline', label: 'Timeline', type: 'text', placeholder: 'Estimated timeline...' }
      ]
    },
    {
      id: 4,
      title: 'Thematic sections',
      description: 'Key topics to cover in the presentation.',
      fields: [
        { key: 'sections', label: 'Sections', type: 'multiselect', options: ['Market Analysis', 'Competitive Landscape', 'Financial Projections', 'Risk Assessment', 'Implementation Plan'] }
      ]
    },
    {
      id: 5,
      title: 'Team details',
      description: 'Information about the team involved.',
      fields: [
        { key: 'teamLead', label: 'Team lead', type: 'text', placeholder: 'Name of team lead...' },
        { key: 'teamSize', label: 'Team size', type: 'text', placeholder: 'Number of team members...' }
      ]
    }
  ];

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev =>
      prev.includes(themeId)
        ? prev.filter(id => id !== themeId)
        : [...prev, themeId]
    );
  };

  const handleExpandAll = () => {
    const centerColumn = document.querySelector('.workspace-center');
    const scrollPos = centerColumn?.scrollTop || 0;
    setExpandedThemes(themes.map(t => t.id));
    requestAnimationFrame(() => {
      if (centerColumn) centerColumn.scrollTop = scrollPos;
    });
  };

  const handleCollapseAll = () => {
    const centerColumn = document.querySelector('.workspace-center');
    const scrollPos = centerColumn?.scrollTop || 0;
    setExpandedThemes([]);
    requestAnimationFrame(() => {
      if (centerColumn) centerColumn.scrollTop = scrollPos;
    });
  };

  const handleClearAll = () => {
    setFormData({
      clientInformation: {
        companyName: '',
        industry: '',
        contactPerson: ''
      },
      documentContext: {
        purpose: '',
        background: ''
      },
      projectScope: {
        objectives: '',
        deliverables: [],
        timeline: ''
      },
      thematicSections: {
        sections: []
      },
      teamDetails: {
        teamLead: '',
        teamSize: ''
      }
    });
  };

  // Initial loading timer - transitions to "ready" state, then auto-exits
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoadingReady(true);
      // Auto-transition to workspace after a brief pause
      setTimeout(() => {
        setOnboardingExiting(true);
        setTimeout(() => {
          setInitialLoading(false);
          requestAnimationFrame(() => {
            const center = document.querySelector('.workspace-center');
            if (center) center.scrollTop = 0;
          });
        }, 700);
      }, 800);
    }, 8000);
    return () => clearTimeout(timer);
  }, [loadingKey]);

  // Handle scroll detection for sticky toolbar
  const toolbarSentinelRef = React.useRef(null);

  useEffect(() => {
    const centerColumn = document.querySelector('.workspace-center');
    if (!centerColumn) return;

    const handleScroll = () => {
      const sentinel = toolbarSentinelRef.current;
      if (!sentinel) return;

      // 16px below the top bar (64px) = 80px from viewport top
      const rect = sentinel.getBoundingClientRect();
      setShowStickyToolbar(rect.top <= 80);
    };

    centerColumn.addEventListener('scroll', handleScroll);
    return () => centerColumn.removeEventListener('scroll', handleScroll);
  }, []);

  // Add welcome message after load with thinking state
  useEffect(() => {
    // Start thinking after workspace fades in
    const thinkingTimer = setTimeout(() => {
      setIsThinking(true);
    }, 1200);

    // Stop thinking and show message after 2 seconds
    const messageTimer = setTimeout(() => {
      setIsThinking(false);
      setChatMessages([{
        role: 'assistant',
        content: "Please fill out the form.\n\nYou can also paste meeting notes, emails, or other relevant material for the Client Proposal here. We'll extract what matters and apply it to the right fields."
      }]);
    }, 3200);

    return () => {
      clearTimeout(thinkingTimer);
      clearTimeout(messageTimer);
    };
  }, []);

  // Calculate global progress
  const calculateGlobalProgress = () => {
    let totalFields = 0;
    let filledFields = 0;

    themes.forEach(theme => {
      const themeKey = titleToCamelCase(theme.title);
      const data = formData[themeKey] || {};

      theme.fields.forEach(field => {
        totalFields++;
        const value = data[field.key];
        if (Array.isArray(value) ? value.length > 0 : value && value.trim() !== '') {
          filledFields++;
        }
      });
    });

    return totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;
  };

  const progress = calculateGlobalProgress();

  // Animate progress counting up
  useEffect(() => {
    if (displayedProgress === progress) return;

    const duration = 400; // milliseconds
    const startValue = displayedProgress;
    const endValue = progress;
    const startTime = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);

      // Ease out function for smoother animation
      const easeOut = 1 - Math.pow(1 - progressRatio, 3);
      const currentValue = Math.round(startValue + (endValue - startValue) * easeOut);

      setDisplayedProgress(currentValue);

      if (progressRatio < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [progress, displayedProgress]);

  // Dynamic color transition from #BABABA to #244AB8
  const getProgressColor = (percentage) => {
    // Start color: #BABABA (186, 186, 186)
    // End color: #244AB8 (36, 74, 184)
    const startR = 186, startG = 186, startB = 186;
    const endR = 36, endG = 74, endB = 184;

    const ratio = percentage / 100;
    const r = Math.round(startR + (endR - startR) * ratio);
    const g = Math.round(startG + (endG - startG) * ratio);
    const b = Math.round(startB + (endB - startB) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  const percentageColor = getProgressColor(displayedProgress);

  const handleFieldChange = (themeKey, fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      [themeKey]: {
        ...prev[themeKey],
        [fieldKey]: value
      }
    }));
  };

  const handleFieldClick = (categoryId, fieldKey) => {
    setFocusedField({ categoryId, fieldKey });
  };

  const handleFieldSubmit = async (categoryId, fieldKey, value, label) => {
    const theme = themes.find(t => t.id === categoryId);
    const themeKey = titleToCamelCase(theme.title);

    handleFieldChange(themeKey, fieldKey, value);
    setFocusedField(null);

    // Add chat acknowledgment for direct input with varied responses
    const responses = [
      `Got it. ${label} added.`,
      `Perfect. ${label} saved.`,
      `Nice. ${label} captured.`,
      `${label} looks good. Keep going.`,
      `${label} noted. What's next?`,
      `Great, ${label} is set.`,
      `${label} confirmed. Moving on.`
    ];
    const randomResponse = responses[Math.floor(Math.random() * responses.length)];

    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: randomResponse
    }]);

    // If company name was submitted, automatically look up industry
    if (themeKey === 'clientInformation' && fieldKey === 'companyName' && value && value.trim() !== '') {
      if (!formData.clientInformation.industry) {
        const industry = await searchCompanyIndustry(value);
        if (industry) {
          handleFieldChange('clientInformation', 'industry', industry);

          // Show a chat message about the auto-fill
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `Updated based on provided information:\n• Company name\n• Industry`
          }]);
        }
      }
    }
  };

  const handleFieldClose = (categoryId, fieldKey) => {
    // Just unfocus the field to return it to completed state
    setFocusedField(null);
  };

  // Helper function to search for company industry
  const searchCompanyIndustry = async (companyName) => {
    try {
      // Mock API call - in production, this would call a real search API
      // For demo purposes, we'll simulate a search with a timeout
      return new Promise((resolve) => {
        setTimeout(() => {
          // Simulate finding industry based on common companies
          const mockResults = {
            'microsoft': 'Technology',
            'google': 'Technology',
            'apple': 'Technology',
            'amazon': 'E-commerce',
            'walmart': 'Retail',
            'coca-cola': 'Beverages',
            'ford': 'Automotive',
            'general electric': 'Conglomerate',
            'jpmorgan': 'Financial Services',
            'bank of america': 'Financial Services',
            'pfizer': 'Pharmaceuticals',
            'united health': 'Healthcare',
            'exxon': 'Energy'
          };

          const lowerName = companyName.toLowerCase();
          for (const [key, industry] of Object.entries(mockResults)) {
            if (lowerName.includes(key) || key.includes(lowerName)) {
              resolve(industry);
              return;
            }
          }

          // Default if not found
          resolve(null);
        }, 300);
      });
    } catch (error) {
      console.error('Error searching for company:', error);
      return null;
    }
  };

  // AI-driven extraction function
  const analyzeInputWithGemini = async (userMessage, focusedFieldContext = null) => {
    const extractedFields = [];
    const lowerMessage = userMessage.toLowerCase();

    // Extract company name from various patterns
    let companyName = null;
    let companyFound = false;

    // Pattern 1: "presentation for [Company]"
    const forPattern = /(?:presentation|proposal|deck|document)\s+for\s+([A-Z][A-Za-z\s&.]+?)(?:[.,]|$|\s+is\s+|\s+in\s+|\s+and\s+)/;
    const forMatch = userMessage.match(forPattern);
    if (forMatch) {
      companyName = forMatch[1].trim();
      companyFound = true;
    }

    // Pattern 2: "company is [Company]" or "client is [Company]"
    if (!companyFound) {
      const isPattern = /(?:company|client|organization|firm|business)(?:\s+name)?\s+is\s+([A-Z][A-Za-z\s&.]+?)(?:[.,]|$|\s+and\s+|\s+in\s+)/;
      const isMatch = userMessage.match(isPattern);
      if (isMatch) {
        companyName = isMatch[1].trim();
        companyFound = true;
      }
    }

    // Pattern 3: Just a capitalized name (if no company already set)
    if (!companyFound && !formData.clientInformation.companyName) {
      const namePattern = /\b([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)*)\b/;
      const nameMatch = userMessage.match(namePattern);
      if (nameMatch && nameMatch[1].length > 2) {
        // Check if it's not a common word
        const commonWords = ['I', 'The', 'This', 'That', 'Create', 'Make', 'Build', 'Presentation'];
        if (!commonWords.includes(nameMatch[1])) {
          companyName = nameMatch[1].trim();
          companyFound = true;
        }
      }
    }

    // If company found, add to extracted fields and search for industry
    if (companyFound && companyName && !formData.clientInformation.companyName) {
      extractedFields.push({
        themeKey: 'clientInformation',
        fieldKey: 'companyName',
        value: companyName
      });

      // Search for industry if not already set
      if (!formData.clientInformation.industry) {
        const industry = await searchCompanyIndustry(companyName);
        if (industry) {
          extractedFields.push({
            themeKey: 'clientInformation',
            fieldKey: 'industry',
            value: industry
          });
        }
      }
    }

    // Extract industry from common industry keywords
    const industryKeywords = {
      'technology': 'Technology', 'tech': 'Technology', 'software': 'Technology', 'it': 'Technology',
      'manufacturing': 'Manufacturing', 'factory': 'Manufacturing', 'production': 'Manufacturing',
      'healthcare': 'Healthcare', 'medical': 'Healthcare', 'hospital': 'Healthcare', 'health': 'Healthcare',
      'finance': 'Finance', 'banking': 'Finance', 'financial': 'Finance',
      'retail': 'Retail', 'store': 'Retail', 'shop': 'Retail',
      'education': 'Education', 'school': 'Education', 'university': 'Education'
    };

    if (!formData.clientInformation.industry) {
      for (const [keyword, industry] of Object.entries(industryKeywords)) {
        if (lowerMessage.includes(keyword)) {
          extractedFields.push({
            themeKey: 'clientInformation',
            fieldKey: 'industry',
            value: industry
          });
          break;
        }
      }
    }

    // Extract contact person names
    const namePatterns = /(?:contact|person|reach out to|speaking with|working with|my name is|i'?m)\s+(?:is\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i;
    const nameMatch = userMessage.match(namePatterns);
    if (nameMatch && !formData.clientInformation.contactPerson) {
      extractedFields.push({
        themeKey: 'clientInformation',
        fieldKey: 'contactPerson',
        value: nameMatch[1]
      });
    }

    // Extract purpose
    const purposePatterns = /(?:purpose is|goal is|aim is|objective is|we want to|we need to|looking to)\s+(.+?)(?:\.|$)/i;
    const purposeMatch = userMessage.match(purposePatterns);
    if (purposeMatch && !formData.documentContext.purpose) {
      extractedFields.push({
        themeKey: 'documentContext',
        fieldKey: 'purpose',
        value: purposeMatch[1].trim()
      });
    }

    // Extract objectives
    const objectivePatterns = /(?:objectives?|goals?|aims?)\s*:?\s*(.+?)(?:\.|$)/i;
    const objectiveMatch = userMessage.match(objectivePatterns);
    if (objectiveMatch && !formData.projectScope.objectives) {
      extractedFields.push({
        themeKey: 'projectScope',
        fieldKey: 'objectives',
        value: objectiveMatch[1].trim()
      });
    }

    // Extract deliverables
    const deliverablesPatterns = /(?:deliverables?|key deliverables?)\s*:?\s*(.+?)(?:\.|$)/i;
    const deliverablesMatch = userMessage.match(deliverablesPatterns);
    if (deliverablesMatch) {
      // Parse the deliverables as a list
      const deliverablesList = deliverablesMatch[1]
        .split(/,|and|\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1));

      if (deliverablesList.length > 0) {
        extractedFields.push({
          themeKey: 'projectScope',
          fieldKey: 'deliverables',
          value: deliverablesList
        });
      }
    }

    // Extract background
    const backgroundPatterns = /(?:background|context|history)\s*:?\s*(.+?)(?:\.|$)/i;
    const backgroundMatch = userMessage.match(backgroundPatterns);
    if (backgroundMatch && !formData.documentContext.background) {
      extractedFields.push({
        themeKey: 'documentContext',
        fieldKey: 'background',
        value: backgroundMatch[1].trim()
      });
    }

    // Extract timeline
    const timelinePatterns = /(?:timeline|timeframe|duration|takes?)\s*:?\s*(?:is\s+)?(.+?)(?:\.|$)/i;
    const timelineMatch = userMessage.match(timelinePatterns);
    if (timelineMatch && !formData.projectScope.timeline) {
      extractedFields.push({
        themeKey: 'projectScope',
        fieldKey: 'timeline',
        value: timelineMatch[1].trim()
      });
    }

    // Extract team lead
    const teamLeadPatterns = /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s+(?:is|as)\s+(?:the\s+)?team\s+lead/i;
    const teamLeadMatch = userMessage.match(teamLeadPatterns);
    if (teamLeadMatch && !formData.teamDetails.teamLead) {
      extractedFields.push({
        themeKey: 'teamDetails',
        fieldKey: 'teamLead',
        value: teamLeadMatch[1].trim()
      });
    }

    // Extract team size
    const teamSizePatterns = /team\s+(?:of|size|has)\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten)/i;
    const teamSizeMatch = userMessage.match(teamSizePatterns);
    if (teamSizeMatch && !formData.teamDetails.teamSize) {
      extractedFields.push({
        themeKey: 'teamDetails',
        fieldKey: 'teamSize',
        value: teamSizeMatch[1].trim()
      });
    }

    // Generate response with field labels
    let assistantResponse = '';
    if (extractedFields.length > 0) {
      const fieldLabels = extractedFields.map(({ themeKey, fieldKey }) => {
        const theme = themes.find(t => titleToCamelCase(t.title) === themeKey);
        const field = theme?.fields.find(f => f.key === fieldKey);
        return field?.label || fieldKey;
      });

      assistantResponse = `Updated based on provided information:\n${fieldLabels.map(label => `• ${label}`).join('\n')}`;
    } else {
      assistantResponse = 'Noted. Please provide more details about the proposal.';
    }

    return {
      extractedFields,
      assistantResponse
    };
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    setChatMessages(prev => [...prev, { role: 'user', content: userInput }]);
    const currentInput = userInput;
    setUserInput('');
    setIsThinking(true);

    // Check if asking about Templafy and Word
    const lowerInput = currentInput.toLowerCase();
    const isAboutTemplafyWord = (lowerInput.includes('templafy') || lowerInput.includes('tempfly')) &&
                                 lowerInput.includes('word');

    if (isAboutTemplafyWord) {
      // Show specific Word response
      setTimeout(() => {
        setIsThinking(false);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `Using Templafy in Word means:

**You always start from the correct document**
Documents are created from centrally managed templates, so you never reuse outdated or unofficial files.

**The document sets itself up**
Your details, company information, and standard content are filled in automatically—no manual setup.

**The document adapts to what you're doing**
Sections, wording, and clauses appear or disappear based on the document type or choices you make.

**Approved content is available while you write**
Insert standard text, clauses, and images directly from Word without copying from old documents.

**Formatting and required content are protected**
Branding, styles, and mandatory text are already applied and can't be accidentally broken.

**AI helps refine your writing, not replace it**
You can use AI to rewrite or improve selected text (clearer, shorter, more formal) inside Word.

**AI stays within company rules**
AI behaviour, tone, and use cases are controlled by the organisation—not left to individual guesswork.

**Everything stays inside Word and company systems**
No external tools, no copy-pasting, and documents can be saved directly to approved locations.`
        }]);
      }, 800);
      return;
    }

    // Analyze input with AI
    const analysis = await analyzeInputWithGemini(currentInput, focusedField);

    // Update extracted fields
    analysis.extractedFields.forEach(({ themeKey, fieldKey, value }) => {
      handleFieldChange(themeKey, fieldKey, value);
    });

    // Show AI response
    setTimeout(() => {
      setIsThinking(false);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: analysis.assistantResponse
      }]);
    }, 800);
  };

  const handleLogoClick = () => {
    const demoData = getDemoPayload();
    setFormData(demoData);

    // Show a quick chat message about demo fill
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Demo data loaded! All fields have been filled with sample content. You can now create your presentation.'
    }]);

    // Expand all themes to show the filled data
    setExpandedThemes(themes.map(t => t.id));
  };

  const handleCreateClick = () => {
    if (progress === 100 && onCreatePresentation) {
      // Clear chat and add loading prompt
      setChatMessages([{
        role: 'assistant',
        content: 'Is there anything about Templafy you would like to know more about?'
      }]);
      onCreatePresentation();
    }
  };

  return (
    <div className="workspace-container">
      {/* Onboarding Loading Overlay */}
      {initialLoading && !isLoading && (
        <div className={'onboarding-overlay' + (onboardingExiting ? ' exiting' : '')}>
          <div className="onboarding-inline" key={loadingKey}>
            <div className="onboarding-inline-header">
              <h2 className="onboarding-title">
                Imagine you work at:{' '}
                <img
                  src="/acme-logo-dark.svg"
                  alt="acme"
                  className="onboarding-acme-logo"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setInitialLoadingReady(false);
                    setOnboardingExiting(false);
                    setStarPositions([]);
                    setLoadingKey(k => k + 1);
                  }}
                />
              </h2>
              <p className="onboarding-description">
                Since we don't have access to your visual identity, we'll show you how it works at Acme. Templafy keeps documents on brand - every time.
              </p>
            </div>
            <div className="onboarding-loader-card">
              <div className={'onboarding-status-row' + (initialLoadingReady ? ' ready' : '')}>
                <span className="onboarding-icon-swap">
                  <svg className="onboarding-sparkle-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 6C12.8011 6 12.6103 5.92098 12.4697 5.78033C12.329 5.63968 12.25 5.44891 12.25 5.25C12.25 4.85218 12.092 4.47064 11.8107 4.18934C11.5294 3.90804 11.1478 3.75 10.75 3.75C10.5511 3.75 10.3603 3.67098 10.2197 3.53033C10.079 3.38968 10 3.19891 10 3C10 2.80109 10.079 2.61032 10.2197 2.46967C10.3603 2.32902 10.5511 2.25 10.75 2.25C11.1478 2.25 11.5294 2.09196 11.8107 1.81066C12.092 1.52936 12.25 1.14782 12.25 0.75C12.25 0.551088 12.329 0.360322 12.4697 0.21967C12.6103 0.0790176 12.8011 0 13 0C13.1989 0 13.3897 0.0790176 13.5303 0.21967C13.671 0.360322 13.75 0.551088 13.75 0.75C13.75 1.14782 13.908 1.52936 14.1893 1.81066C14.4706 2.09196 14.8522 2.25 15.25 2.25C15.4489 2.25 15.6397 2.32902 15.7803 2.46967C15.921 2.61032 16 2.80109 16 3C16 3.19891 15.921 3.38968 15.7803 3.53033C15.6397 3.67098 15.4489 3.75 15.25 3.75C14.8522 3.75 14.4706 3.90804 14.1893 4.18934C13.908 4.47064 13.75 4.85218 13.75 5.25C13.75 5.44891 13.671 5.63968 13.5303 5.78033C13.3897 5.92098 13.1989 6 13 6Z" fill="currentColor"/>
                    <path d="M6.5 15C6.25688 15 6.02373 14.9034 5.85182 14.7315C5.67991 14.5596 5.58333 14.3264 5.58333 14.0833C5.58333 13.1109 5.19702 12.1782 4.50939 11.4906C3.82176 10.803 2.88913 10.4167 1.91667 10.4167C1.67355 10.4167 1.44039 10.3201 1.26849 10.1482C1.09658 9.97627 1 9.74312 1 9.5C1 9.25688 1.09658 9.02373 1.26849 8.85182C1.44039 8.67991 1.67355 8.58333 1.91667 8.58333C2.88913 8.58333 3.82176 8.19703 4.50939 7.50939C5.19702 6.82176 5.58333 5.88913 5.58333 4.91667C5.58333 4.67355 5.67991 4.44039 5.85182 4.26849C6.02373 4.09658 6.25688 4 6.5 4C6.74312 4 6.97627 4.09658 7.14818 4.26849C7.32009 4.44039 7.41667 4.67355 7.41667 4.91667C7.41667 5.88913 7.80298 6.82176 8.49061 7.50939C9.17824 8.19703 10.1109 8.58333 11.0833 8.58333C11.3264 8.58333 11.5596 8.67991 11.7315 8.85182C11.9034 9.02373 12 9.25688 12 9.5C12 9.74312 11.9034 9.97627 11.7315 10.1482C11.5596 10.3201 11.3264 10.4167 11.0833 10.4167C10.1109 10.4167 9.17824 10.803 8.49061 11.4906C7.80298 12.1782 7.41667 13.1109 7.41667 14.0833C7.41667 14.3264 7.32009 14.5596 7.14818 14.7315C6.97627 14.9034 6.74312 15 6.5 15Z" fill="currentColor"/>
                  </svg>
                  <svg className="onboarding-check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.11376 14.2097L0.456906 8.55286L2.34206 6.6677L6.11307 10.4387L13.6558 2.89601L15.5416 4.78185L6.11376 14.2097Z" fill="#464646"/>
                  </svg>
                </span>
                <div className="onboarding-progress-text-wrapper">
                  <p className={'onboarding-progress-text' + (initialLoadingReady ? ' ready' : '')}>
                    Preparing smart questions
                  </p>
                  <p className={'onboarding-progress-text-done' + (initialLoadingReady ? ' visible' : '')}>
                    Smart questions ready
                  </p>
                </div>
              </div>
              <div className="onboarding-inline-body">
                <div className={'onboarding-skeleton-layer' + (initialLoadingReady ? ' ready' : '')}>
                  {[0, 1, 2, 3, 4, 5].map((groupIdx) => (
                    <div key={groupIdx} className="onboarding-skeleton-group">
                      <div className="onboarding-dot-grid">
                        {Array.from({ length: dotsPerGroup }).map((_, i) => {
                          const globalIdx = groupIdx * dotsPerGroup + i;
                          const starKey = starCellMap[globalIdx];
                          return (
                            <div
                              key={i}
                              className={`onboarding-dot${starKey !== undefined ? ' has-star' : ''}`}
                              style={{ animationDelay: `${dotDelays[globalIdx]}s` }}
                            >
                              {starKey !== undefined && (
                                <img
                                  key={`star-${starKey}`}
                                  src="/sparkle-star.svg"
                                  alt=""
                                  className="onboarding-star-img"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {initialLoadingReady && (
                        <div className="onboarding-skeleton-bar-solid" style={{ animationDelay: `${groupIdx * 0.08}s` }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Left Sidebar */}
      <div className="workspace-sidebar">
        <div className="sidebar-header">
          <img
            src="/Templafy-logo-white.svg"
            alt="Templafy"
            className="sidebar-logo"
            onClick={onLogoClick}
            style={{ cursor: 'pointer' }}
          />
        </div>

        {/* Share Section - Bottom Aligned */}
        <div className="sidebar-share">
          <h3 className="sidebar-share-title">Like what you're seeing? Don't keep it to yourself</h3>
          <p className="sidebar-share-text">
            Now share it. With your manager. Your team.
            Anyone who hates wasting time as much as you do.
            <br /><br />
            More shares = better shot at making Templafy part of your daily life.
          </p>
          <div className="sidebar-share-buttons">
            <button className="sidebar-share-button" onClick={handleLinkedInShare}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M16 1H4a3 3 0 00-3 3v12a3 3 0 003 3h12a3 3 0 003-3V4a3 3 0 00-3-3zM6 16H4V7h2v9zM5 6a1 1 0 110-2 1 1 0 010 2zm11 10h-2v-4.5c0-1.1-.9-2-2-2s-2 .9-2 2V16H8V7h2v1c.6-.6 1.5-1 2.5-1 1.9 0 3.5 1.6 3.5 3.5V16z" fill="currentColor"/>
              </svg>
              LinkedIn
            </button>
            <button className="sidebar-share-button" onClick={handleCopyLink}>
              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                <g>
                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M8.995,7.005 L8.995,7.005c1.374,1.374,1.374,3.601,0,4.975l-1.99,1.99c-1.374,1.374-3.601,1.374-4.975,0l0,0c-1.374-1.374-1.374-3.601,0-4.975 l1.748-1.698"></path>
                  <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M7.005,8.995 L7.005,8.995c-1.374-1.374-1.374-3.601,0-4.975l1.99-1.99c1.374-1.374,3.601-1.374,4.975,0l0,0c1.374,1.374,1.374,3.601,0,4.975 l-1.748,1.698"></path>
                </g>
              </svg>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      {/* Top Bar - spans from left menu to right edge */}
      <div className="workspace-header">
        <div className="header-left">
          <h1 className="workspace-title">New presentation</h1>
        </div>
        <div className="header-right">
          <button className="back-to-overview" onClick={onBack}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
              <g fill="currentColor" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="19" x2="5" y2="5" fill="none" stroke="currentColor" strokeWidth="2"></line>
                <line x1="19" y1="5" x2="5" y2="19" fill="none" stroke="currentColor" strokeWidth="2"></line>
              </g>
            </svg>
            Back to overview
          </button>
        </div>
      </div>

      {/* Fixed Sticky Toolbar */}
      <div className={`fixed-toolbar ${showStickyToolbar ? 'visible' : ''}`}>
        <span className="document-toolbar-progress" style={{ color: percentageColor }}>
          {displayedProgress}% complete
        </span>
        <div className="document-toolbar-actions">
          <button className="control-button" onClick={handleExpandAll}>
            Expand all
          </button>
          <div className="control-separator"></div>
          <button className="control-button" onClick={handleCollapseAll}>
            Collapse all
          </button>
        </div>
      </div>

      {/* Center Column */}
      <div className={'workspace-center' + (isLoading ? ' loading-state' : '')}>
        {isLoading ? (
          <CreatingView onComplete={onLoadingComplete} documentTitle={documentTitle} />
        ) : !initialLoading ? (
          <div className="workspace-content-wrapper">

        <div className="themes-container">
          {/* Document Header */}
          <div className="document-header slide-up-in" style={{ animationDelay: '0s' }}>
            <h2 className="document-title">{documentTitle}</h2>
            <p className="document-subtitle">Fill in the details below to create your presentation</p>
          </div>
          <div ref={toolbarSentinelRef} className="toolbar-sentinel" />
          <div className={`document-toolbar slide-up-in ${showStickyToolbar ? 'hidden-toolbar' : ''}`} style={{ animationDelay: '0s' }}>
            <span className="document-toolbar-progress" style={{ color: percentageColor }}>
              {displayedProgress}% complete
            </span>
            <div className="document-toolbar-actions">
              <button className="control-button" onClick={handleExpandAll}>
                Expand all
              </button>
              <div className="control-separator"></div>
              <button className="control-button" onClick={handleCollapseAll}>
                Collapse all
              </button>
            </div>
          </div>

          {themes.map((theme, themeIndex) => {
            const themeKey = titleToCamelCase(theme.title);
            const isExpanded = expandedThemes.includes(theme.id);

            return (
              <div key={theme.id} className="theme-block slide-up-in" style={{ animationDelay: `${(themeIndex + 1) * 0.08}s` }}>
                <div className="theme-header" onClick={() => toggleTheme(theme.id)}>
                  <div className="theme-header-left">
                    <svg
                      className={`theme-chevron ${isExpanded ? 'expanded' : ''}`}
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path d="M7 5l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3 className="theme-title">{theme.title}</h3>
                  </div>
                  <div className="category-progress">
                    {theme.fields.map((field, i) => {
                      const value = formData[themeKey]?.[field.key] || '';
                      const isFilled = Array.isArray(value) ? value.length > 0 : value.trim() !== '';
                      return (
                        <div
                          key={i}
                          className={`progress-segment ${isFilled ? 'filled' : ''}`}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className={`theme-fields ${isExpanded ? 'expanded' : ''}`}>
                  <p className="field-description">{theme.description}</p>
                  <div className="field-rows-container">
                    <div className="field-rows-container-inner">
                      {theme.fields.map((field, fieldIndex) => {
                        const value = formData[themeKey]?.[field.key] || '';
                        const fieldWithValue = { ...field, value };
                        const isFieldFocused = focusedField?.categoryId === theme.id && focusedField?.fieldKey === field.key;
                        const isEmpty = Array.isArray(value) ? value.length === 0 : !value || (typeof value === 'string' && value.trim() === '');

                        // Find index of first empty field
                        const firstEmptyFieldIndex = theme.fields.findIndex(f => {
                          const v = formData[themeKey]?.[f.key] || '';
                          return Array.isArray(v) ? v.length === 0 : !v || (typeof v === 'string' && v.trim() === '');
                        });

                        const shouldAutoFocus = fieldIndex === firstEmptyFieldIndex && isEmpty;

                        return (
                          <FieldRow
                            key={field.key}
                            field={fieldWithValue}
                            categoryId={theme.id}
                            isActive={isFieldFocused || isEmpty}
                            shouldAutoFocus={shouldAutoFocus}
                            onInteract={() => handleFieldClick(theme.id, field.key)}
                            onSubmit={handleFieldSubmit}
                            onClear={handleFieldClose}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Clear all and Fill with example buttons */}
          <div className="clear-all-container slide-up-in" style={{ animationDelay: `${(themes.length + 1) * 0.08}s` }}>
            <button className="fill-example-button" onClick={handleLogoClick}>
              Use sample answers
            </button>
            <div className="button-separator"></div>
            <button className="clear-all-button" onClick={handleClearAll}>
              Clear answers
            </button>
          </div>
        </div>

            <div className="workspace-footer slide-up-in" style={{ animationDelay: `${(themes.length + 2) * 0.08}s` }}>
              <button
                className="create-button"
                disabled={progress < 100}
                onClick={handleCreateClick}
              >
                Create presentation
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Right Sidebar - AI Chat */}
      <div className={`workspace-right ${isLoading ? 'full-height' : ''}`} style={initialLoading && !isLoading ? { visibility: 'hidden' } : {}}>
        <div className={`chat-container ${isLoading ? 'no-bottom-bar' : ''}`}>
          <div className="chat-messages">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="message-content">{renderMessageContent(msg.content)}</div>
              </div>
            ))}
            {isThinking && (
              <div className="chat-message assistant">
                <div className="thinking-indicator">
                  <img src="/Loader-small.gif" alt="Loading" className="thinking-loader" />
                  <span className="thinking-text">Thinking</span>
                </div>
              </div>
            )}
          </div>
          <div className="chat-input-container">
            <textarea
              className="chat-input"
              placeholder="Type your message..."
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              rows={3}
            />
            <div className="chat-toolbar">
              <button
                className={`send-button ${userInput.trim() ? 'active' : ''}`}
                onClick={handleSendMessage}
                disabled={!userInput.trim()}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6 9V3M6 3l-3 3M6 3l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Workspace;
